// F-09 · Tableros: bandeja general (HU-34), indicadores de gestión (HU-35), trazabilidad del
// caso (HU-37) y el estado de la farmacovigilancia del Dashboard (HU-34b). Contrato: `administracion/interfaces/http/esquemas.py` (AT S3 §2.2.8) y las
// rutas de `rutas_de_tableros.py`.
//
// LA PANTALLA NO CALCULA NADA DE LO QUE AQUÍ LLEGA (`estandares_frontend.md` §8.1 R1). El
// semáforo de cada caso (con su texto, su ícono y el reloj que rige), el valor de cada
// indicador con su meta y si se cumple, y la línea de tiempo con el texto de cada hito los
// deriva el servidor. El ámbito del usuario también: un país fuera de él es un 403 con el texto
// del contrato, y la pantalla lo muestra tal cual.

import { pedir, pedirBinario, seg, segPais, segUuid } from './cliente';

// ---------------------------------------------------------------------------
// HU-34 · bandeja general
// ---------------------------------------------------------------------------

/** `SemaforoSalida`: código, texto e ícono, siempre los tres (RNF-11). */
export type Semaforo = {
  estado: string;
  texto: string;
  icono: string;
};

/** `CasoEnBandejaSalida`: una fila. Ni un campo de la sección de personas (RN-42). */
export type CasoEnBandeja = {
  caso_id: string;
  numero: string | null;
  pais: string;
  nombre_pais: string | null;
  estado: string;
  gravedad: string | null;
  fecha_recepcion: string;
  fecha_registro: string | null;
  fecha_presentacion: string | null;
  producto_principal: string | null;
  afecta_a_personas: boolean;
  /** «Afecta a personas: sí» o `null` (HU-34-C6). */
  texto_de_personas: string | null;
  semaforo: Semaforo;
  /** Lo que dice la columna de plazo tal cual: «Vencido · notificación» (C3, C4, C7, C8). */
  texto_del_semaforo: string;
  icono_del_semaforo: string;
  /** `notificacion`, `informe_final` o `ninguno` (C7). */
  reloj_que_rige: string;
  plazo_fiable: boolean;
  fecha_vencimiento: string | null;
  /** El estado como lo dice la Figura 11 del AF S3: «En seguimiento» (§8.1 R1). Vacío con un servidor anterior. */
  texto_del_estado?: string;
  /** «Grave», «No grave» o «Sin decidir» (ui/MG-ILE-67). Vacío con un servidor anterior. */
  texto_de_la_gravedad?: string;
  // ui/MG-ILE-67 · rediseño de la fila (operador, 5-oct-2026). Todo lo arma el servidor: la
  // pantalla no calcula fechas, horas ni textos.
  /** La etapa del flujo en que está el caso (código) y su texto. */
  etapa?: string;
  texto_de_la_etapa?: string;
  /** Quién debe mover el caso ahora; `null` si está cerrado. */
  responsable_actual?: string | null;
  /** Cuándo entró el caso en su etapa actual. */
  en_la_etapa_desde?: string;
  /** Horas hábiles en la etapa según el horario de oficina; `null` sin horario o cerrado. */
  horas_habiles_en_la_etapa?: number | null;
  /** «6 h», «2 d 3 h», «Sin horario»; `null` si está cerrado. */
  texto_en_la_etapa?: string | null;
  /** Lo que dice la columna «Plazo» en negrita: «Vencido hace 2 días», «Sin plazo», «No aplica». */
  texto_corto_del_plazo?: string;
  /** La línea pequeña bajo el plazo: «Vencía 03/10/2026 · notificación»; en «Sin plazo», la ayuda. */
  detalle_del_plazo?: string | null;
  /** CC-02 (B10, AT S3 §11.3.8): las autoridades a las que se notifica, en el orden del trámite. */
  destinos?: string[];
  /** Los destinos en una línea: «SENASA · SANIPES». */
  texto_de_los_destinos?: string | null;
};

/** Por qué columna ordena el servidor la bandeja (ui/MG-ILE-67, «Orden por cabecera»). */
export type OrdenDeLaBandeja =
  | 'urgencia' | 'numero' | 'pais' | 'producto' | 'etapa' | 'gravedad' | 'plazo' | 'responsable' | 'en_la_etapa' | 'recibido';
export type DireccionDelOrden = 'asc' | 'desc';

export type BandejaGeneral = {
  casos: CasoEnBandeja[];
  total: number;
  pagina: number;
  por_pagina: number;
  /** Cuándo se armó esta lista (ui/MG-ILE-67, «Refrescar»); nulo con un servidor anterior. */
  consultada_en?: string | null;
};

/** Los filtros de `GET /tableros/bandeja`. Vacío = no se manda (todos). */
export type FiltrosDeLaBandejaGeneral = {
  pais?: string;
  producto_sku?: string;
  estado?: string;
  gravedad?: string;
  numero?: string;
  recepcion_desde?: string;
  recepcion_hasta?: string;
  registro_desde?: string;
  registro_hasta?: string;
  presentacion_desde?: string;
  presentacion_hasta?: string;
};

/** Los parámetros de la consulta, sin los vacíos: el filtro «todos» es no mandar la clave. */
function consulta(parametros: Record<string, string | number | undefined>): string {
  const p = new URLSearchParams();
  for (const [clave, valor] of Object.entries(parametros)) {
    if (valor !== undefined && valor !== '') p.set(clave, String(valor).trim());
  }
  const texto = p.toString();
  return texto ? `?${texto}` : '';
}

/**
 * HU-34. La página pedida de la bandeja, ya ordenada por el servidor (C1: por urgencia si no se
 * pide otro orden) y filtrada por el ámbito del usuario (C5): la pantalla no reordena ni recorta.
 * Sin `orden` (o con `urgencia`) no se manda la clave: el servidor ordena por urgencia.
 */
export function consultarBandejaGeneral(
  filtros: FiltrosDeLaBandejaGeneral,
  { pagina = 1, porPagina = 50, orden, direccion, senal }: {
    pagina?: number; porPagina?: number; orden?: OrdenDeLaBandeja; direccion?: DireccionDelOrden; senal?: AbortSignal;
  } = {},
) {
  if (filtros.pais) segPais(filtros.pais);
  const conOrden = orden && orden !== 'urgencia' ? { orden, direccion: direccion ?? 'asc' } : {};
  return pedir<BandejaGeneral>(
    `/tableros/bandeja${consulta({ ...filtros, ...conOrden, pagina, por_pagina: porPagina })}`,
    { senal },
  );
}

// ---------------------------------------------------------------------------
// HU-35 · indicadores de gestión
// ---------------------------------------------------------------------------

/** `IndicadorSalida`: el indicador ya calculado, con su ficha (fórmula, meta). */
export type Indicador = {
  codigo: string;
  nombre: string;
  formula: string;
  /** `horas`, `dias_habiles`, `porcentaje` o `razon`. */
  unidad: string;
  meta: number | null;
  /** «≤ 24 h», «100 %» (HU-35-C2). */
  texto_de_meta: string;
  meta_confirmada_por_el_cliente: boolean;
  /** `null` sin casos: nunca 0 % ni 100 % (C4). Un porcentaje va en base 100. */
  valor: number | null;
  hay_datos: boolean;
  cumple_la_meta: boolean | null;
  /** «Cumple», «No cumple» o «Sin casos en el periodo» (C2, C4). */
  texto_de_cumplimiento: string;
  icono: string;
  numerador: number | null;
  denominador: number;
  explicacion: string;
  limitaciones: string[];
};

export type Tablero = {
  desde: string;
  hasta: string;
  indicadores: Indicador[];
};

/** `FechaDeLaMedicionSalida`: la etiqueta y el valor que explican un caso del indicador. */
export type FechaDeLaMedicion = { etiqueta: string; valor: string };

/** `CasoDelIndicadorSalida` (HU-35-C6). */
export type CasoDelIndicador = {
  caso_id: string;
  numero: string | null;
  resultado: string;
  /** «En plazo», «Fuera de plazo», «Cumplido»… */
  texto_del_resultado: string;
  detalle: string | null;
  fechas: FechaDeLaMedicion[];
  /**
   * CC-02 (B10, AT S3 §11.3.9): en los indicadores que se miden por carta, la autoridad de la
   * carta medida (un caso con dos destinos aparece dos veces). `autoridad` es su nombre y solo
   * llega cuando el caso tiene más de una carta.
   */
  autoridad_codigo?: string | null;
  autoridad?: string | null;
  /** RN-82: el país del caso, para leer sus horas en su zona. Ausente con un servidor anterior (Lima). */
  pais?: string | null;
};

export type CasosDelIndicador = {
  desde: string;
  hasta: string;
  indicador: Indicador;
  /** Solo los casos de la página pedida (paginación del servidor, de 10 en 10). */
  casos: CasoDelIndicador[];
  /** Cuántos casos componen el indicador (su denominador), no los de esta página. */
  total: number;
  pagina: number;
  por_pagina: number;
};

/**
 * El recorte del tablero: el periodo es obligatorio (inclusivo); el país y la autoridad,
 * opcionales. La autoridad (su código, CC-02) recorta los indicadores que se miden por carta.
 */
export type RecorteDelTablero = { desde: string; hasta: string; pais?: string; autoridad?: string };

const recorte = ({ desde, hasta, pais, autoridad }: RecorteDelTablero) => {
  if (pais) segPais(pais);
  return consulta({ desde, hasta, pais, autoridad });
};

/** HU-35. Los ocho indicadores del periodo dentro del ámbito (C3). */
export function consultarIndicadores(filtros: RecorteDelTablero, { senal }: { senal?: AbortSignal } = {}) {
  return pedir<Tablero>(`/tableros/indicadores${recorte(filtros)}`, { senal });
}

/**
 * HU-35-C6. Los casos que componen UN indicador, en el mismo recorte que el tablero, una página a
 * la vez: el servidor ordena todo el conjunto y devuelve `total` (operador, 5-oct-2026).
 */
export function consultarCasosDelIndicador(
  codigo: string,
  filtros: RecorteDelTablero,
  { pagina = 1, porPagina = 10, senal }: { pagina?: number; porPagina?: number; senal?: AbortSignal } = {},
) {
  const { desde, hasta, pais } = filtros;
  if (pais) segPais(pais);
  return pedir<CasosDelIndicador>(
    `/tableros/indicadores/${seg(codigo)}/casos${consulta({ desde, hasta, pais, pagina, por_pagina: porPagina })}`,
    { senal },
  );
}

// ---------------------------------------------------------------------------
// HU-37 · trazabilidad del caso
// ---------------------------------------------------------------------------

/** `HitoSalida`. La pantalla pinta `texto`, nunca `evento` (AT S3 §2.3). */
export type Hito = {
  ocurrido_en: string;
  evento: string;
  texto: string;
  actor: string;
  rama: string;
  dato_caducado: boolean;
  version: number | null;
  direccion_ip: string | null;
  motivo: string | null;
  /** El dato guardado es un día sin hora: se muestra solo la fecha. */
  solo_fecha: boolean;
};

export type Trazabilidad = {
  caso_id: string;
  numero: string | null;
  pais: string;
  afecta_a_personas: boolean;
  /** Siempre `false` (HU-37-C5). */
  incluye_contenido_l2: boolean;
  /** Las ramas que el caso tiene, en el orden de las columnas (HU-37-C2). */
  ramas: string[];
  /** Ya ordenados por `ocurrido_en` ascendente. */
  hitos: Hito[];
  acciones_permitidas: string[];
};

/** HU-37. La línea de tiempo del caso; fuera del ámbito, 403 sin ningún hito (C8). */
export function consultarTrazabilidad(casoId: string, { senal }: { senal?: AbortSignal } = {}) {
  return pedir<Trazabilidad>(`/tableros/trazabilidad/${segUuid(casoId)}`, { senal });
}

/**
 * HU-37-C9. La vista imprimible que sirve el servidor (P-04, opción b: el PDF lo produce el
 * navegador al imprimir). Se pide por el cliente —y no con un enlace— para que un 403 o un 404
 * lleguen como `Fallo` con el texto del contrato y no como un JSON en una pestaña nueva.
 */
export function exportarTrazabilidad(casoId: string) {
  return pedirBinario(`/tableros/trazabilidad/${segUuid(casoId)}/exportar`);
}

// ---------------------------------------------------------------------------
// Seguimiento del caso (modal de la bandeja; operador, 5-oct-2026)
// ---------------------------------------------------------------------------

/** Una de las seis etapas del flujo, con su estado ya resuelto por el servidor. */
export type EtapaDelSeguimiento = {
  codigo: string;
  texto: string;
  estado: 'hecha' | 'actual' | 'pendiente';
  /** Cuándo entró el caso en la etapa; `null` si aún no entró. */
  entro_el: string | null;
};

/** Los datos del resumen, ya redactados por el servidor: la pantalla no calcula duraciones. */
export type ResumenDelSeguimiento = {
  texto_en_la_etapa: string | null;
  texto_desde_la_recepcion: string | null;
  texto_corto_del_plazo: string;
  detalle_del_plazo: string | null;
};

/** Un hito de la línea de tiempo; `etapa` nula = anotación o aviso que no abre etapa. */
export type HitoDelSeguimiento = {
  ocurrido_en: string;
  solo_fecha: boolean;
  texto: string;
  actor: string | null;
  etapa: string | null;
  /** «2 h 15 min desde el hito anterior», «punto de partida» para el primero. */
  texto_desde_el_anterior: string;
};

export type SeguimientoDelCaso = {
  caso_id: string;
  numero: string | null;
  texto_del_estado: string;
  etapa: string;
  texto_de_la_etapa: string;
  responsable_actual: string | null;
  etapas: EtapaDelSeguimiento[];
  resumen: ResumenDelSeguimiento;
  /** Ya ordenados por `ocurrido_en` ascendente. */
  hitos: HitoDelSeguimiento[];
};

/** El flujo del caso por etapas, su resumen y sus hitos; fuera del ámbito, 403 con el texto del contrato. */
export function consultarSeguimiento(casoId: string, { senal }: { senal?: AbortSignal } = {}) {
  return pedir<SeguimientoDelCaso>(`/tableros/seguimiento/${segUuid(casoId)}`, { senal });
}

// ---------------------------------------------------------------------------
// HU-34b · Dashboard: foto del estado de la farmacovigilancia (AT S3 §12.0)
// ---------------------------------------------------------------------------

/** `CifrasSalida`. Cada conteo llega hecho; la pantalla no suma casos (§8.1 R1). */
export type CifrasDelEstado = {
  casos_activos: number;
  casos_en_total: number;
  /** Activos graves (D-34b-1). */
  casos_graves: number;
  /** Casos con al menos una carta enviada sin su cargo, una vez cada uno. */
  acuses_pendientes: number;
  /**
   * Solo con permiso L2. Sin permiso la clave NO viene (ni `null` ni 0, decisión 2): la tarjeta
   * no existe.
   */
  casos_con_datos_de_personas?: number;
};

/** Uno de los tres casos más urgentes, con el texto del plazo de su fila de la bandeja (V1). */
export type CasoEnRiesgo = {
  caso_id: string;
  /** El esquema lo admite nulo; un caso enviado siempre tiene número (RN-01). */
  numero: string | null;
  pais: string;
  nombre_pais: string | null;
  /** «SENASA · SANIPES»; `null` sin destinos (se muestra el país). */
  texto_de_los_destinos: string | null;
  semaforo: Semaforo;
  reloj_que_rige: string;
  fecha_vencimiento: string | null;
  /** «Vencido hace 2 días»; ausente con un servidor anterior (se pinta `semaforo.texto`). */
  texto_corto_del_plazo?: string;
  /** «Vencía 05/10/2026». */
  detalle_del_plazo?: string | null;
  /** `false` si la fecha límite se calculó sin el calendario del país (HU-24). */
  plazo_fiable: boolean;
};

export type PlazosDelEstado = {
  vencidos: number;
  por_vencer: number;
  /** De 0 a 3, en el orden de urgencia de la bandeja (HU-34-C1). */
  mas_urgentes: CasoEnRiesgo[];
};

/** Un mes de la ventana de 12 (`mes` = `aaaa-mm`, `texto` = «nov 2025»). */
export type CasosDelMes = { mes: string; texto: string; casos: number; graves: number };

/** Uno de los nueve estados, con ceros, en el orden del servidor. */
export type CasosDelEstado = { estado: string; texto: string; casos: number };

/** Uno de los cinco países con más casos, en el orden del servidor. */
export type CasosDelPais = { pais: string; nombre_pais: string; casos: number };

/** Un caso que espera un paso (`acuse_pendiente`, `informe_observado` o `esperando_evaluacion`). */
export type ElementoDeAtencion = {
  motivo: string;
  /** «Notificado sin acuse de SENASA y SANIPES», armado por el servidor. */
  texto: string;
  caso_id: string;
  numero: string | null;
  pais: string;
  nombre_pais: string | null;
  autoridades: string[];
  /** Solo en `acuse_pendiente`: fecha sin hora (no se convierte, RN-82). */
  fecha_presentacion: string | null;
  en_la_etapa_desde: string | null;
  /** Solo en `informe_observado` y `esperando_evaluacion`: «5 h», «Sin horario». */
  texto_en_la_etapa: string | null;
};

/** `EstadoSalida` de `GET /tableros/estado`. */
export type EstadoDeLaFarmacovigilancia = {
  consultado_en: string;
  cifras: CifrasDelEstado;
  plazos: PlazosDelEstado;
  /** Siempre 12, del mes más antiguo al actual (hora de Lima). */
  casos_por_mes: CasosDelMes[];
  /** Siempre 9, sin `borrador`. */
  casos_por_estado: CasosDelEstado[];
  casos_por_pais: CasosDelPais[];
  /** Sin tope (D-34b-2), en el orden del servidor. */
  atencion_requerida: ElementoDeAtencion[];
};

/**
 * HU-34b. La foto del estado dentro del ámbito del usuario. Sin parámetros: el recorte es el
 * ámbito y nada más. 403 sin el módulo; 422 si un usuario no corporativo no tiene países.
 */
export function consultarEstado({ senal }: { senal?: AbortSignal } = {}) {
  return pedir<EstadoDeLaFarmacovigilancia>('/tableros/estado', { senal });
}
